/**
 * One hypothesis on the Investigations page (PRD-0075 R-E1): its claim, its current state (the
 * latest verdict and confidence), the strip of every assessment, and how much it rests on.
 */
import { Lightbulb } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import type { AnalyticHypothesis, AssessmentPoint } from '@/types/analytic-hypothesis'
import { AssessmentStrip } from './AssessmentStrip'
import { VERDICT_BADGE } from './strip'

const COPY = 'analyticHypotheses'

/** Where a hypothesis's strip ends: when it was closed, else now. */
export function stripEnd(hypothesis: Pick<AnalyticHypothesis, 'closed_at'>): string {
  return hypothesis.closed_at ?? new Date().toISOString()
}

/** The latest verdict and confidence as one badge; a muted one before any assessment. */
export function VerdictBadge({
  point,
}: {
  point: Pick<AssessmentPoint, 'verdict' | 'confidence'> | null
}) {
  const { t } = useTranslation()
  if (!point) return <Badge variant="muted">{t(`${COPY}.card.notAssessed`)}</Badge>
  return (
    <Badge variant={VERDICT_BADGE[point.verdict]}>
      {t(`${COPY}.verdict.${point.verdict}`)}
      <span aria-hidden>·</span>
      {t(`${COPY}.confidence.${point.confidence}`)}
    </Badge>
  )
}

export interface HypothesisCardProps {
  hypothesis: AnalyticHypothesis
  /** The hypothesis page. */
  to: string
}

export function HypothesisCard({ hypothesis, to }: HypothesisCardProps) {
  const { t } = useTranslation()
  const closed = hypothesis.status === 'closed'
  return (
    <Card asChild interactive className="flex w-full flex-col gap-3 p-4 text-left">
      <Link to={to} aria-label={t(`${COPY}.card.open`, { title: hypothesis.title })}>
        <div className="flex flex-wrap items-center gap-2">
          <Lightbulb aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
            {hypothesis.title}
          </span>
          {closed && <Badge variant="muted">{t(`${COPY}.status.closed`)}</Badge>}
        </div>
        <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">
          {hypothesis.claim}
        </p>
        <div>
          <VerdictBadge point={hypothesis.latest} />
        </div>
        <AssessmentStrip points={hypothesis.strip} end={stripEnd(hypothesis)} />
        <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-2xs text-muted-foreground">
          <span>{t(`${COPY}.card.assessments`, { count: hypothesis.strip.length })}</span>
          <span>{t(`${COPY}.card.focus`, { count: hypothesis.focus_uids.length })}</span>
        </div>
      </Link>
    </Card>
  )
}
